"use client";

import * as React from "react";
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { CheckIcon, ChevronRightIcon, CircleIcon } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * ContextMenu Component
 *
 * A context menu component that appears on right-click.
 * Built on top of Radix UI Context Menu primitive.
 *
 * @example
 * ```tsx
 * <ContextMenu>
 *   <ContextMenuTrigger>Right click me</ContextMenuTrigger>
 *   <ContextMenuContent>
 *     <ContextMenuItem>Copy</ContextMenuItem>
 *     <ContextMenuItem>Paste</ContextMenuItem>
 *   </ContextMenuContent>
 * </ContextMenu>
 * ```
 */
function ContextMenu({ ...props }: React.ComponentProps<typeof ContextMenuPrimitive.Root>) {
    return <ContextMenuPrimitive.Root data-slot="context-menu" {...props} />;
}
ContextMenu.displayName = "ContextMenu";

/**
 * ContextMenuTrigger Component
 *
 * The trigger element that activates the context menu.
 */
function ContextMenuTrigger({ ...props }: React.ComponentProps<typeof ContextMenuPrimitive.Trigger>) {
    return <ContextMenuPrimitive.Trigger data-slot="context-menu-trigger" {...props} />;
}
ContextMenuTrigger.displayName = "ContextMenuTrigger";

/**
 * ContextMenuGroup Component
 *
 * Groups related context menu items.
 */
function ContextMenuGroup({ ...props }: React.ComponentProps<typeof ContextMenuPrimitive.Group>) {
    return <ContextMenuPrimitive.Group data-slot="context-menu-group" {...props} />;
}
ContextMenuGroup.displayName = "ContextMenuGroup";

/**
 * ContextMenuPortal Component
 *
 * Portal for rendering context menu content.
 */
function ContextMenuPortal({ ...props }: React.ComponentProps<typeof ContextMenuPrimitive.Portal>) {
    return <ContextMenuPrimitive.Portal data-slot="context-menu-portal" {...props} />;
}
ContextMenuPortal.displayName = "ContextMenuPortal";

/**
 * ContextMenuSub Component
 *
 * Sub-menu container for nested context menus.
 */
function ContextMenuSub({ ...props }: React.ComponentProps<typeof ContextMenuPrimitive.Sub>) {
    return <ContextMenuPrimitive.Sub data-slot="context-menu-sub" {...props} />;
}
ContextMenuSub.displayName = "ContextMenuSub";

/**
 * ContextMenuRadioGroup Component
 *
 * Radio group for mutually exclusive context menu items.
 */
function ContextMenuRadioGroup({ ...props }: React.ComponentProps<typeof ContextMenuPrimitive.RadioGroup>) {
    return <ContextMenuPrimitive.RadioGroup data-slot="context-menu-radio-group" {...props} />;
}
ContextMenuRadioGroup.displayName = "ContextMenuRadioGroup";

/**
 * ContextMenuSubTrigger Component
 *
 * Trigger for sub-menu items.
 */
function ContextMenuSubTrigger({
    className,
    inset,
    children,
    ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.SubTrigger> & {
    inset?: boolean;
}) {
    return (
        <ContextMenuPrimitive.SubTrigger
            data-slot="context-menu-sub-trigger"
            data-inset={inset}
            className={cn(
                "setu:focus:bg-accent setu:focus:text-accent-foreground setu:data-[state=open]:bg-accent setu:data-[state=open]:text-accent-foreground setu:flex setu:cursor-default setu:items-center setu:rounded-sm setu:px-2 setu:py-1.5 setu:text-sm setu:outline-hidden setu:select-none setu:data-[inset]:pl-8 setu:[&_svg]:pointer-events-none setu:[&_svg]:shrink-0 setu:[&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        >
            {children}
            <ChevronRightIcon className="setu:ml-auto" />
        </ContextMenuPrimitive.SubTrigger>
    );
}
ContextMenuSubTrigger.displayName = "ContextMenuSubTrigger";

/**
 * ContextMenuSubContent Component
 *
 * Content container for sub-menus.
 */
function ContextMenuSubContent({ className, ...props }: React.ComponentProps<typeof ContextMenuPrimitive.SubContent>) {
    return (
        <ContextMenuPrimitive.SubContent
            data-slot="context-menu-sub-content"
            className={cn(
                "setu:bg-popover setu:text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 setu:z-50 setu:min-w-[8rem] setu:origin-(--radix-context-menu-content-transform-origin) setu:overflow-hidden setu:rounded-md setu:!border setu:p-1 setu:shadow-lg",
                className
            )}
            {...props}
        />
    );
}
ContextMenuSubContent.displayName = "ContextMenuSubContent";

/**
 * ContextMenuContent Component
 *
 * Main content container for the context menu.
 */
function ContextMenuContent({ className, ...props }: React.ComponentProps<typeof ContextMenuPrimitive.Content>) {
    return (
        <ContextMenuPrimitive.Portal>
            <ContextMenuPrimitive.Content
                data-slot="context-menu-content"
                className={cn(
                    "setu:bg-popover setu:text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 setu:z-50 setu:max-h-(--radix-context-menu-content-available-height) setu:min-w-[8rem] setu:origin-(--radix-context-menu-content-transform-origin) setu:overflow-x-hidden setu:overflow-y-auto setu:rounded-md setu:!border setu:p-1 setu:shadow-md",
                    className
                )}
                {...props}
            />
        </ContextMenuPrimitive.Portal>
    );
}
ContextMenuContent.displayName = "ContextMenuContent";

/**
 * ContextMenuItem Component
 *
 * Individual item in the context menu.
 */
function ContextMenuItem({
    className,
    inset,
    variant = "default",
    ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.Item> & {
    inset?: boolean;
    variant?: "default" | "destructive";
}) {
    return (
        <ContextMenuPrimitive.Item
            data-slot="context-menu-item"
            data-inset={inset}
            data-variant={variant}
            className={cn(
                "setu:focus:bg-accent setu:focus:text-accent-foreground setu:data-[variant=destructive]:text-destructive setu:data-[variant=destructive]:focus:bg-destructive/10 dark:setu:data-[variant=destructive]:focus:bg-destructive/20 setu:data-[variant=destructive]:focus:text-destructive setu:data-[variant=destructive]:*:[svg]:!text-destructive setu:[&_svg:not([class*='text-'])]:text-muted-foreground setu:relative setu:flex setu:cursor-default setu:items-center setu:gap-2 setu:rounded-sm setu:px-2 setu:py-1.5 setu:text-sm setu:outline-hidden setu:select-none setu:data-[disabled]:pointer-events-none setu:data-[disabled]:opacity-50 setu:data-[inset]:pl-8 setu:[&_svg]:pointer-events-none setu:[&_svg]:shrink-0 setu:[&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        />
    );
}
ContextMenuItem.displayName = "ContextMenuItem";

/**
 * ContextMenuCheckboxItem Component
 *
 * Checkbox item in the context menu.
 */
function ContextMenuCheckboxItem({
    className,
    children,
    checked,
    ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.CheckboxItem>) {
    return (
        <ContextMenuPrimitive.CheckboxItem
            data-slot="context-menu-checkbox-item"
            className={cn(
                "setu:focus:bg-accent setu:focus:text-accent-foreground setu:relative setu:flex setu:cursor-default setu:items-center setu:gap-2 setu:rounded-sm setu:py-1.5 setu:pr-2 setu:pl-8 setu:text-sm setu:outline-hidden setu:select-none setu:data-[disabled]:pointer-events-none setu:data-[disabled]:opacity-50 setu:[&_svg]:pointer-events-none setu:[&_svg]:shrink-0 setu:[&_svg:not([class*='size-'])]:size-4",
                className
            )}
            checked={checked}
            {...props}
        >
            <span className="setu:pointer-events-none setu:absolute setu:left-2 setu:flex setu:size-3.5 setu:items-center setu:justify-center">
                <ContextMenuPrimitive.ItemIndicator>
                    <CheckIcon className="setu:size-4" />
                </ContextMenuPrimitive.ItemIndicator>
            </span>
            {children}
        </ContextMenuPrimitive.CheckboxItem>
    );
}
ContextMenuCheckboxItem.displayName = "ContextMenuCheckboxItem";

/**
 * ContextMenuRadioItem Component
 *
 * Radio item in the context menu.
 */
function ContextMenuRadioItem({
    className,
    children,
    ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.RadioItem>) {
    return (
        <ContextMenuPrimitive.RadioItem
            data-slot="context-menu-radio-item"
            className={cn(
                "setu:focus:bg-accent setu:focus:text-accent-foreground setu:relative setu:flex setu:cursor-default setu:items-center setu:gap-2 setu:rounded-sm setu:py-1.5 setu:pr-2 setu:pl-8 setu:text-sm setu:outline-hidden setu:select-none setu:data-[disabled]:pointer-events-none setu:data-[disabled]:opacity-50 setu:[&_svg]:pointer-events-none setu:[&_svg]:shrink-0 setu:[&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        >
            <span className="setu:pointer-events-none setu:absolute setu:left-2 setu:flex setu:size-3.5 setu:items-center setu:justify-center">
                <ContextMenuPrimitive.ItemIndicator>
                    <CircleIcon className="setu:size-2 setu:fill-current" />
                </ContextMenuPrimitive.ItemIndicator>
            </span>
            {children}
        </ContextMenuPrimitive.RadioItem>
    );
}
ContextMenuRadioItem.displayName = "ContextMenuRadioItem";

/**
 * ContextMenuLabel Component
 *
 * Label for context menu sections.
 */
function ContextMenuLabel({
    className,
    inset,
    ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.Label> & {
    inset?: boolean;
}) {
    return (
        <ContextMenuPrimitive.Label
            data-slot="context-menu-label"
            data-inset={inset}
            className={cn(
                "setu:text-foreground setu:px-2 setu:py-1.5 setu:text-sm setu:font-medium setu:data-[inset]:pl-8",
                className
            )}
            {...props}
        />
    );
}
ContextMenuLabel.displayName = "ContextMenuLabel";

/**
 * ContextMenuSeparator Component
 *
 * Visual separator between context menu items.
 */
function ContextMenuSeparator({ className, ...props }: React.ComponentProps<typeof ContextMenuPrimitive.Separator>) {
    return (
        <ContextMenuPrimitive.Separator
            data-slot="context-menu-separator"
            className={cn("setu:!bg-border setu:-mx-1 setu:my-1 setu:h-px", className)}
            {...props}
        />
    );
}
ContextMenuSeparator.displayName = "ContextMenuSeparator";

/**
 * ContextMenuShortcut Component
 *
 * Keyboard shortcut display for context menu items.
 */
function ContextMenuShortcut({ className, ...props }: React.ComponentProps<"span">) {
    return (
        <span
            data-slot="context-menu-shortcut"
            className={cn("setu:text-muted-foreground setu:ml-auto setu:text-xs setu:tracking-widest", className)}
            {...props}
        />
    );
}
ContextMenuShortcut.displayName = "ContextMenuShortcut";

export {
    ContextMenu,
    ContextMenuTrigger,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuCheckboxItem,
    ContextMenuRadioItem,
    ContextMenuLabel,
    ContextMenuSeparator,
    ContextMenuShortcut,
    ContextMenuGroup,
    ContextMenuPortal,
    ContextMenuSub,
    ContextMenuSubContent,
    ContextMenuSubTrigger,
    ContextMenuRadioGroup,
};
